import type { VertexTopology } from '@/origami/topology';
import { THEOREM_TOLERANCE, toDegrees } from '@/constants/numeric';

export interface KawasakiResult {
  vertex: number;
  degree: number;
  applicable: boolean;
  /** Why the theorem does not apply, when it does not. */
  notApplicableReason?: 'boundary-vertex' | 'odd-degree' | 'degree-too-low';
  /**
   * Alternating sum a1 - a2 + a3 - ... , radians. Zero iff Kawasaki holds.
   * Only the MAGNITUDE is meaningful: which sector is called a1 depends on where
   * the counter-clockwise ordering happens to start, and shifting the start by
   * one flips the sign.
   */
  residual: number;
  residualDegrees: number;
  /** Sum of odd-indexed sectors (1-based), radians. Should be pi. */
  oddSum: number;
  /** Sum of even-indexed sectors, radians. Should be pi. */
  evenSum: number;
  satisfied: boolean;
  sectors: number[];
  /** Indices of the sectors that contribute most to the residual. */
  worstSectors: number[];
}

/**
 * Kawasaki-Justin theorem (necessary condition for local flat-foldability).
 *
 * A single interior vertex of degree 2n with consecutive sector angles
 * a_1 ... a_2n (counter-clockwise) folds flat only if
 *
 *     a_1 - a_2 + a_3 - ... - a_2n = 0
 *
 * equivalently  a_1 + a_3 + ... = a_2 + a_4 + ... = pi.
 *
 * The condition is NECESSARY AND SUFFICIENT for a *single* vertex, but says
 * nothing about whether a whole crease pattern folds flat: global flat
 * foldability is NP-hard (Bern & Hayes 1996). Odd-degree interior vertices can
 * never fold flat, and we report that as "not applicable" rather than "fails",
 * because the alternating sum is not even well defined there.
 *
 * Reference: T. Kawasaki, "On the relation between mountain-creases and
 * valley-creases of a flat origami" (1989); J. Justin (1986).
 */
export function analyzeKawasaki(v: VertexTopology): KawasakiResult {
  const sectors = v.sectors;
  const base: KawasakiResult = {
    vertex: v.index,
    degree: v.degree,
    applicable: false,
    residual: 0,
    residualDegrees: 0,
    oddSum: 0,
    evenSum: 0,
    satisfied: false,
    sectors,
    worstSectors: [],
  };

  if (!v.interior) return { ...base, notApplicableReason: 'boundary-vertex' };
  if (v.degree % 2 !== 0) return { ...base, notApplicableReason: 'odd-degree' };
  if (v.degree < 4) return { ...base, notApplicableReason: 'degree-too-low' };

  let residual = 0;
  let oddSum = 0;
  let evenSum = 0;
  for (let i = 0; i < sectors.length; i++) {
    if (i % 2 === 0) {
      residual += sectors[i];
      oddSum += sectors[i];
    } else {
      residual -= sectors[i];
      evenSum += sectors[i];
    }
  }

  // Sectors whose removal would most reduce |residual|.
  const ranked = sectors
    .map((a, i) => ({ i, score: Math.abs(a - Math.PI / (sectors.length / 2)) }))
    .sort((p, q) => q.score - p.score)
    .slice(0, 2)
    .map((r) => r.i);

  return {
    vertex: v.index,
    degree: v.degree,
    applicable: true,
    residual,
    residualDegrees: toDegrees(residual),
    oddSum,
    evenSum,
    satisfied: Math.abs(residual) <= THEOREM_TOLERANCE,
    sectors,
    worstSectors: Math.abs(residual) <= THEOREM_TOLERANCE ? [] : ranked,
  };
}
